import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useMapsLibrary } from "@vis.gl/react-google-maps";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { centerLatLng } from "@/screens/dispatch-v2/center-location";

interface SelectedAddress {
  line1: string;
  line2: string;
  city: string;
  province: string;
  country: string;
  postalCode: string;
  location: { lat: number; lng: number };
}

/** Same Places prediction/details flow as the admin address selector. */
export function AddressSearch({
  disabled,
  onSelect,
  onLoadingChange,
}: {
  disabled: boolean;
  onSelect: (address: SelectedAddress) => void;
  onLoadingChange: (loading: boolean) => void;
}) {
  const places = useMapsLibrary("places");
  const services = useMemo(
    () =>
      places
        ? {
            autocomplete: new places.AutocompleteService(),
            details: new places.PlacesService(document.createElement("div")),
            token: new places.AutocompleteSessionToken(),
          }
        : null,
    [places]
  );
  const [query, setQuery] = useState("");
  const [predictions, setPredictions] = useState<
    google.maps.places.AutocompletePrediction[]
  >([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const requestId = useRef(0);
  const deadline = useRef<number | undefined>(undefined);
  const stopDeadline = useCallback(
    () => window.clearTimeout(deadline.current),
    []
  );
  const startDeadline = useCallback(
    (id: number) => {
      stopDeadline();
      deadline.current = window.setTimeout(() => {
        if (id !== requestId.current) return;
        requestId.current++;
        setLoading(false);
        setPredictions([]);
        setError("Address lookup timed out. Edit your search to try again.");
      }, 10_000);
    },
    [stopDeadline]
  );
  useEffect(
    () => () => {
      requestId.current++;
      window.clearTimeout(deadline.current);
    },
    []
  );

  useEffect(() => {
    onLoadingChange(loading);
  }, [loading, onLoadingChange]);

  useEffect(() => {
    const id = ++requestId.current;
    if (!services || !query.trim()) return;
    const timer = window.setTimeout(() => {
      setLoading(true);
      startDeadline(id);
      services.autocomplete
        .getPlacePredictions({
          input: query,
          sessionToken: services.token,
          componentRestrictions: { country: "ca" },
        })
        .then((response) => {
          if (id !== requestId.current) return;
          setPredictions(response.predictions);
          if (!response.predictions.length)
            setError("No addresses found. Try a more complete address.");
        })
        .catch(() => {
          if (id === requestId.current)
            setError("Address lookup failed. Edit your search to try again.");
        })
        .finally(() => {
          if (id === requestId.current) {
            stopDeadline();
            setLoading(false);
          }
        });
    }, 250);
    return () => {
      window.clearTimeout(timer);
      stopDeadline();
      requestId.current++;
    };
  }, [query, services, startDeadline, stopDeadline]);

  const select = (placeId: string) => {
    if (!services) return;
    const id = ++requestId.current;
    setLoading(true);
    startDeadline(id);
    setError(null);
    services.details.getDetails(
      {
        placeId,
        sessionToken: services.token,
        fields: ["address_components", "geometry"],
      },
      (place, status) => {
        if (id !== requestId.current) return;
        stopDeadline();
        setLoading(false);
        const point = place?.geometry?.location;
        const location = point
          ? centerLatLng({
              address: { location: { lat: point.lat(), lng: point.lng() } },
            })
          : null;
        const component = (type: string, short = false) => {
          const part = place?.address_components?.find((value) =>
            value.types.includes(type)
          );
          return (short ? part?.short_name : part?.long_name) ?? "";
        };
        const address = {
          line1: `${component("street_number")} ${component("route")}`.trim(),
          line2: component("subpremise"),
          city: component("locality") || component("postal_town"),
          province: component("administrative_area_level_1", true),
          country: component("country", true),
          postalCode: component("postal_code"),
        };
        if (
          status !== "OK" ||
          !location ||
          !address.line1 ||
          !address.city ||
          !address.province ||
          !address.country
        ) {
          setError(
            "This result has no complete address or usable map location. Select another address."
          );
          return;
        }
        onSelect({ ...address, location });
        setQuery("");
        setPredictions([]);
      }
    );
  };

  return (
    <div className="space-y-2">
      <label htmlFor="dispatch-address-search" className="text-sm font-medium">
        Find address
      </label>
      <Input
        id="dispatch-address-search"
        value={query}
        disabled={disabled || !services}
        placeholder="Search a Canadian street address"
        onChange={(event) => {
          requestId.current++;
          stopDeadline();
          setQuery(event.target.value);
          setPredictions([]);
          setError(null);
          setLoading(false);
        }}
      />
      {!services && (
        <p className="text-sm text-muted-foreground">
          Address search is unavailable. Reload to retry or contact your
          administrator.
        </p>
      )}
      {loading && (
        <p role="status" className="text-sm text-muted-foreground">
          Looking up address…
        </p>
      )}
      {error && (
        <p role="status" className="text-sm text-destructive">
          {error}
        </p>
      )}
      {predictions.length > 0 && (
        <ul aria-label="Address suggestions" className="rounded-md border">
          {predictions.map((prediction) => (
            <li key={prediction.place_id}>
              <Button
                type="button"
                variant="ghost"
                className="h-auto w-full justify-start whitespace-normal text-left"
                disabled={disabled || loading}
                onClick={() => select(prediction.place_id)}
              >
                {prediction.description}
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
